import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import { round6 as round } from 'utilities/math';
import {
  isRequiredNumber,
  isRequiredNumberGreaterThan,
  isRequiredIntegerGreaterThan
} from 'utilities/validators.js';

import Input from 'components/Input';
import Dropdown from '../Dropdown';
import { RadioButtonsGroup } from 'scenes/InferentialStatistics/SettingsForm/RadioButton/Component';

const isPositiveNumber = isRequiredNumberGreaterThan(0);
const isMoreThanOneInteger = isRequiredIntegerGreaterThan(1);

const MAX_NUMBER_OF_CHARACTERS = 8;

const groupRadioButtons = [{
  value: 'differenceData',
  name: 'datasetContains',
  labelClassName: 'mr-10',
  label: 'Difference Data'

}, {
  value: 'rawData',
  name: 'datasetContains',
  label: 'Raw Data'
}];

class PairedTComponent extends React.PureComponent {
  formatFn = value => (value || value === 0 ? round(value) : '');

  formatMean = value => {
    if (!value) {
      return 'N/A';
    }
    if (value === 'N/A') {
      return value;
    }
    const stringValue = round(value).toString();
    if (stringValue.length > MAX_NUMBER_OF_CHARACTERS) {
      return stringValue.slice(0, MAX_NUMBER_OF_CHARACTERS).concat('...');
    }
    return stringValue;
  };

  listOfNumericalVariables = this.props.getNumericalOptionsDataSet();

  isDataSetAvailable = this.listOfNumericalVariables && this.listOfNumericalVariables.length > 1;

  renderTextLikeInput = (label, testId, mean) =>
    <div className="relative width-32" data-testid={testId}>
      <div className="main-input__label width-70">{label}</div>
      <div className="text-likeInput">{this.formatMean(mean)}</div>
    </div>

  isDropDownHasError = name => !name;

  renderDropDownsAndStaticText = () => {
    const {
      getNumericalOptionsDataSet,
      onGenerateMeanDataSetFirst,
      onGenerateMeanDataSetSecond,
      sampleMeanFirst,
      sampleMeanSecond,
      sampleSizeFirst,
      sampleSizeSecond,
      standardDeviationFirst,
      standardDeviationSecond,
      secondVariable,
      firstVariable
    } = this.props;
    return (
      <>
        <Dropdown
          isClearable
          optionsList={getNumericalOptionsDataSet(secondVariable)}
          label="Sample Variable #1"
          name="numericalRowDataSetFirst"
          placeholder="Select a Variable #1"
          onChange={onGenerateMeanDataSetFirst}
          id="chosen-row-dataSet-dropdown-first"
          labelClassName="font-900 font-14 mb-5"
          isError={this.isDropDownHasError(firstVariable)}
        />

        <div className="flex space-between mb-15 baseline">
          { this.renderTextLikeInput('Sample Mean', 'sampleMean1', sampleMeanFirst)}
          { this.renderTextLikeInput('Sample St. Dev.', 'stDev1', standardDeviationFirst)}
          { this.renderTextLikeInput('Sample Size', 'sampleSize1', sampleSizeFirst)}
        </div>

        <Dropdown
          isClearable
          optionsList={getNumericalOptionsDataSet(firstVariable)}
          label="Sample Variable #2"
          name="numericalRowDataSetSecond"
          placeholder="Select a Variable #2"
          onChange={onGenerateMeanDataSetSecond}
          id="chosen-row-dataSet-dropdown-second"
          labelClassName="font-900 font-14 mb-5"
          isError={this.isDropDownHasError(secondVariable)}
        />

        <div className="flex space-between mb-15 baseline">
          { this.renderTextLikeInput('Sample Mean', 'sampleMean2', sampleMeanSecond)}
          { this.renderTextLikeInput('Sample St. dev.', 'stDev2', standardDeviationSecond)}
          { this.renderTextLikeInput('Sample Size', 'sampleSize2', sampleSizeSecond)}
        </div>
      </>
    );
  }

  renderInputs = () => {
    const {
      onGenerateMeanDataSet,
      onSampleSizeChange,
      onSampleMeanDiffChange,
      onStandardDeviationDiffChange
    } = this.props;
    return (
      <>
        {
          this.listOfNumericalVariables.length > 0 &&
            <Dropdown
              isClearable
              optionsList={this.listOfNumericalVariables}
              label="Sample Variable"
              name="numericalRowDataSet"
              placeholder="Select a Variable"
              onChange={onGenerateMeanDataSet}
              id="chosen-row-dataSet-dropdown"
              labelClassName="font-900 font-14 mb-5"
            />
        }
        <div className="flex space-between mb-15 baseline">
          <Field
            type="number"
            id="sampleMeanDiff"
            component={Input}
            name="sampleMeanDiff"
            label="Mean of Sample Diff."
            pattern="\d+"
            format={this.formatFn}
            validate={isRequiredNumber}
            onChange={onSampleMeanDiffChange}
            wrapperClassName="relative width-32 iconTop two-lines"
            inputClassName="main-input__input"
            labelClassName="main-input__label width-70"
          />

          <Field
            type="number"
            id="standardDeviationDiff"
            component={Input}
            name="standardDeviationDiff"
            label="St. Dev.\nof Sample\nDiff."
            pattern="\d+"
            min={1}
            format={this.formatFn}
            validate={isPositiveNumber}
            onChange={onStandardDeviationDiffChange}
            wrapperClassName="relative width-32 iconTop two-lines"
            inputClassName="main-input__input"
            labelClassName="main-input__label width-80"
          />

          <Field
            type="number"
            id="sampleSize"
            component={Input}
            name="sampleSize"
            label="Sample Size"
            pattern="\d+"
            min={2}
            format={this.formatFn}
            validate={isMoreThanOneInteger}
            onChange={onSampleSizeChange}
            wrapperClassName="relative width-32 iconTop two-lines"
            inputClassName="main-input__input"
            labelClassName="main-input__label width-70"
          />
        </div>
      </>
    );
  };

  renderFormPairedTDataSetNOTAvailable = () => this.renderInputs();

  renderFormPairedTDataSetAvailable = () => {
    const { isDatasetContainsRawData } = this.props;
    return (
      <>
        <RadioButtonsGroup
          labelGroupRadioButton="Dataset Contains"
          buttons={groupRadioButtons}
        />
        {
          isDatasetContainsRawData
            ? this.renderDropDownsAndStaticText()
            : this.renderInputs()
        }
      </>
    );
  }

  render () {
    return this.isDataSetAvailable ? this.renderFormPairedTDataSetAvailable()
      : this.renderFormPairedTDataSetNOTAvailable();
  }
}

PairedTComponent.propTypes = {
  onGenerateMeanDataSet: PropTypes.func.isRequired,
  getNumericalOptionsDataSet: PropTypes.func.isRequired,
  onGenerateMeanDataSetFirst: PropTypes.func.isRequired,
  onGenerateMeanDataSetSecond: PropTypes.func.isRequired,
  sampleMeanFirst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sampleSizeFirst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  standardDeviationFirst: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sampleSizeSecond: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sampleMeanSecond: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  standardDeviationSecond: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSampleSizeChange: PropTypes.func.isRequired,
  onSampleMeanDiffChange: PropTypes.func.isRequired,
  onStandardDeviationDiffChange: PropTypes.func.isRequired,
  firstVariable: PropTypes.string,
  secondVariable: PropTypes.string,
  isDatasetContainsRawData: PropTypes.bool
};

export default PairedTComponent;
